import { effect, inject, Injectable, signal } from '@angular/core';
import { ICustomerRegistration } from './customer-registration.model';
import { Company } from '@shared-ikp/model/company.model';
import { ApiService } from '../api/api.service';
import { EMPTY, lastValueFrom, map, Observable } from 'rxjs';
import { API_ROUTE_CREATE_CUSTOMER, API_ROUTE_EMAIL_EXISTANCE_CHECK, getApiUrl } from '../../app.constants';
import { IRegistrationRequest } from '@shared-ikp/model/registration.model';
import { toObservable } from '@angular/core/rxjs-interop';
import { CustomerRegistrationSessionstorageService } from './customer-registration-sessionstorage.service';
import { HttpResponse } from '@angular/common/http';
import { AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { IBerater } from '../../model/berater-http.model';

@Injectable({
  providedIn: 'root',
})
export class CustomerRegistrationService extends ApiService {
  private readonly customerRegistrationSessionStorage = inject(CustomerRegistrationSessionstorageService);

  preSelectedGpNr = signal<string | null>(null);

  isNextButtonDisabled = signal<boolean>(true);
  isNextButtonDisabled$ = toObservable(this.isNextButtonDisabled);
  isRegistrationError = signal<boolean>(false);

  constructor() {
    super();

    effect(() => {
      this.customerRegistrationSessionStorage.storeRegistrationState(this.registrationModel());
    });
  }

  public registrationModel = signal<ICustomerRegistration>(
    this.customerRegistrationSessionStorage.retrieveRegistrationState() ?? {
      companies: [],
      berater: null,
      email: '',
    },
  );

  toggleCompany(company: Company): void {
    this.registrationModel.update(prev => {
      const unternehmen = prev.companies.find(u => u.gpNr === company.gpNr);
      if (unternehmen) {
        return { ...prev, companies: prev.companies.filter(u => u.gpNr !== company.gpNr) };
      } else {
        return { ...prev, companies: [...prev.companies, company] };
      }
    });
  }

  addCompany(company: Company): void {
    this.registrationModel.update(prev => {
      const unternehmen = prev.companies.find(u => u.gpNr === company.gpNr);
      if (unternehmen) {
        return { ...prev };
      }
      return { ...prev, companies: [...prev.companies, company] };
    });
  }

  clear(): void {
    this.registrationModel.set({
      companies: [],
      berater: null,
      email: '',
    });
  }

  setSelectedBerater(berater: IBerater): void {
    this.registrationModel.update(prev => ({ ...prev, berater }));
  }

  setSelectedEmail(email: string): void {
    this.registrationModel.update(prev => ({ ...prev, email }));
  }

  async createCustomer(): Promise<void> {
    const requestBody: IRegistrationRequest = {
      gpNumbers: this.registrationModel().companies.map(company => company.gpNr),
      adminUserEmailAddress: this.registrationModel().email,
      kundenberaterId: this.registrationModel().berater.id,
    };
    await lastValueFrom(
      this.httpClient.post(getApiUrl(this.apiUrl, API_ROUTE_CREATE_CUSTOMER), requestBody, { observe: 'response' }),
    ).catch(() => this.isRegistrationError.set(true));
  }

  existsEmail(emailAddress: string): Observable<boolean> {
    return this.httpClient
      .get(getApiUrl(this.apiUrl, API_ROUTE_EMAIL_EXISTANCE_CHECK), {
        observe: 'response',
        params: { emailAddress: emailAddress },
      })
      .pipe(
        map((response: HttpResponse<boolean>) => {
          if (response.status === 200) {
            return response.body;
          }
          return false;
        }),
      );
  }

  updateEmailAndResendInvitation(accountId: string, newEmailAddress: string): Observable<HttpResponse<unknown>> {
    return this.httpClient.put(
      getApiUrl(this.apiUrl, `account/${accountId}/email`),
      { emailAddress: newEmailAddress },
      { observe: 'response' },
    );
  }

  emailAlreadyInUse(): AsyncValidatorFn {
    return (control): Observable<ValidationErrors | null> => {
      if (!control.value) {
        return EMPTY;
      }

      return this.existsEmail(control.value).pipe(
        map(emailInUse => (emailInUse ? { translationKey: 'inputErrors.alreadyInUse.email' } : null)),
      );
    };
  }
}
